import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Check, X, Clock, ArrowsLeftRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TransferRequestsPanel = ({ onTransferComplete }) => {
  const { axiosInstance, isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/workers/transfer-requests?status=pending');
      setRequests(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [axiosInstance]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleAccept = async (id) => {
    setProcessing(id);
    try {
      await axiosInstance.put(`/api/workers/transfer-requests/${id}/accept`);
      toast.success('Traspaso aceptado');
      loadRequests();
      onTransferComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aceptar');
    } finally { setProcessing(null); }
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await axiosInstance.put(`/api/workers/transfer-requests/${id}/reject`);
      toast.success('Solicitud rechazada');
      loadRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al rechazar');
    } finally { setProcessing(null); }
  };

  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 p-4 space-y-3" data-testid="transfer-requests-panel">
      <div className="flex items-center gap-2">
        <ArrowsLeftRight size={18} className="text-amber-600" />
        <h3 className="text-sm font-bold text-amber-800">
          Solicitudes de Traspaso Pendientes ({requests.length})
        </h3>
      </div>
      {requests.map(req => (
        <div key={req.id} className="bg-white border border-amber-200 p-3 flex items-center gap-3" data-testid={`transfer-req-${req.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-900 truncate">
              {req.worker_name} <span className="text-xs font-mono text-zinc-400">({req.worker_code})</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-0.5">
              <span className="font-medium">{req.from_branch_name}</span>
              <ArrowRight size={12} className="text-amber-500" />
              <span className="font-medium">{req.to_branch_name}</span>
            </div>
            <div className="text-[10px] text-zinc-400 mt-0.5">
              Solicitado por: {req.requested_by_name}
            </div>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button
              onClick={() => handleAccept(req.id)}
              disabled={processing === req.id}
              className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
              data-testid={`accept-transfer-${req.id}`}
            >
              <Check size={14} /> Aceptar
            </button>
            <button
              onClick={() => handleReject(req.id)}
              disabled={processing === req.id}
              className="px-3 py-1.5 text-xs font-medium border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors disabled:opacity-50 flex items-center gap-1"
              data-testid={`reject-transfer-${req.id}`}
            >
              <X size={14} /> Rechazar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TransferRequestsPanel;
